import React from "react"
import { FormContainer, Worksheet, LabeledInput } from "uikit"
import { Grid, Dropdown } from "semantic-ui-react"
import { Footer } from "./Footer"
import { useStore } from "store"

const PrivateSchoolForm = ({
  data: {
    fields: {
      person_id,
      school_type,
      start_month,
      start_year,
      amountChoice,
      amount,
      years_duration,
      externalPercentChoice,
      external_percent
    },
    isModified,
    onSubmit,
    isPending,
    hasError,
    error
  },
  submitLabel = "Done",
  submitHeader = "",
  onDelete,
  baseUrl,
  schoolTypeOptions,
  monthOptions,
  externalPercentOptions,
  peopleDropdownOptions,
  getPersonYearOptions,
  getAmountOptions,
  getSchoolTypeText,
  getYearDurationOptions,
  isNew
}) => (
  <Worksheet error={error}>
    <Worksheet.Body>
      <Worksheet.Row label="Who are you planning for?">
        <Worksheet.Field error={person_id.error}>
          <Dropdown
            {...person_id}
            selection
            fluid
            placeholder="Person"
            options={peopleDropdownOptions}
          />
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row label="What type of private education are you planning for?">
        <Worksheet.Field error={school_type.error}>
          <Dropdown
            {...school_type}
            selection
            fluid
            placeholder="School Type"
            options={schoolTypeOptions}
          />
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row
        label={
          "When does the person plan to start " +
          getSchoolTypeText(school_type) +
          "?"
        }
      >
        <Worksheet.Field error={start_year.error}>
          <Grid>
            <Grid.Row>
              <Grid.Column width={7}>
                <Dropdown
                  selection
                  {...start_month}
                  fluid
                  placeholder="Month"
                  options={monthOptions}
                />
              </Grid.Column>
              <Grid.Column width={9}>
                <Dropdown
                  selection
                  {...start_year}
                  fluid
                  placeholder="Year"
                  options={getPersonYearOptions(person_id.value)}
                />
              </Grid.Column>
            </Grid.Row>
          </Grid>
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row
        label={
          "How many years of " +
          getSchoolTypeText(school_type) +
          " are you planning for?"
        }
      >
        <Worksheet.Field error={years_duration.error}>
          <Dropdown
            selection
            fluid
            {...years_duration}
            options={getYearDurationOptions(person_id.value, start_year.value)}
          />
        </Worksheet.Field>
      </Worksheet.Row>
      <Worksheet.Row
        label={
          "What tier of " +
          getSchoolTypeText(school_type) +
          " are you planning for?"
        }
      >
        <Worksheet.Field error={amountChoice.error}>
          <Dropdown
            selection
            fluid
            {...amountChoice}
            onChange={(event, data) => {
              if (data.value !== "Other") {
                amount.onChange(event, data)
              }
              amountChoice.onChange(event, data)
            }}
            options={getAmountOptions(school_type.value)}
          />
        </Worksheet.Field>
        {amountChoice.value === "Other" || !!amount.error ? (
          <Worksheet.Row label="How much per year?" paddedField>
            <Worksheet.Field error={amount.error}>
              <LabeledInput
                {...amount}
                onBlur={event => {
                  amount.onBlur(event)
                }}
                label=""
                iconText="$"
                iconPosition="left"
                placeholder="0"
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
      </Worksheet.Row>

      <Worksheet.Row label="What portion to be paid by financial aid or relatives?">
        <Worksheet.Field error={externalPercentChoice.error}>
          <Dropdown
            {...externalPercentChoice}
            onChange={(event, data) => {
              if (data.value !== "Other") {
                external_percent.onChange(event, data)
              }
              externalPercentChoice.onChange(event, data)
            }}
            options={externalPercentOptions}
            selection
          />
        </Worksheet.Field>
        {externalPercentChoice.value === "Other" || !!external_percent.error ? (
          <Worksheet.Row
            label=" What exact portion will your financial aid or relatives cover?"
            paddedField
          >
            <Worksheet.Field error={external_percent.error}>
              <LabeledInput
                {...external_percent}
                onBlur={event => {
                  if (
                    externalPercentOptions.find(
                      option => option.value === external_percent.value
                    )
                  ) {
                  }
                  external_percent.onBlur(event)
                }}
                label=""
                iconText="%"
                iconPosition="right"
                placeholder="0"
              />
            </Worksheet.Field>
          </Worksheet.Row>
        ) : null}
      </Worksheet.Row>
    </Worksheet.Body>

    <Footer
      baseUrl={baseUrl}
      isNew={isNew}
      submitLabel={submitLabel}
      onSubmit={onSubmit}
      isPending={isPending}
      onDelete={onDelete}
      isModified={isModified}
      deleteLabel="Delete Goal"
      error={error}
    />
  </Worksheet>
)

const PrivateSchoolFormContainer = FormContainer(
  {
    person_id: {
      type: "string"
    },
    start_year: {
      type: "unit",
      useGrouping: false
    },
    start_month: {
      type: "unit",
      defaultValue: 9
    },
    school_type: {
      type: "string"
    },
    amountChoice: {
      type: "string",
      required: false,
      submit: false
    },
    amount: {
      type: "unit",
      minValue: 1000
    },
    years_duration: {
      type: "unit",
      minValue: 1,
      maxValue: 15
    },
    externalPercentChoice: {
      type: "string",
      required: false,
      submit: false
    },
    external_percent: {
      type: "unit",
      minValue: 0,
      maxValue: 100
    },
    type: {
      type: "string",
      defaultValue: "privateschool"
    }
  },
  (fields, ...form) => {}
)(PrivateSchoolForm)

const PrivateSchool = ({ household, goal, scenario, ...formProps }) => {
  const [allPeople, { addGoal, updateGoal, deleteGoal }] = useStore(
    "householdPeople",
    household.id
  )

  const isNew = goal === null

  let values = isNew ? {} : goal
  const currentYear = new Date().getFullYear()
  const people = Object.values(allPeople).filter(
    x => currentYear - new Date(x.birth_date).getFullYear() <= 21
  )
  const peopleDropdownOptions = people.map(person => ({
    value: `${person.id}`,
    text: person.first_name
  }))

  const schoolTypeOptions = [
    { value: "primary", text: "Primary School" },
    { value: "secondary", text: "High School" }
  ]
  const monthOptions = [
    { value: "1", text: "January" },
    { value: "2", text: "February" },
    { value: "3", text: "March" },
    { value: "4", text: "April" },
    { value: "5", text: "May" },
    { value: "6", text: "June" },
    { value: "7", text: "July" },
    { value: "8", text: "August" },
    { value: "9", text: "September" },
    { value: "10", text: "October" },
    { value: "11", text: "November" },
    { value: "12", text: "December" }
  ]

  const externalPercentOptions = [
    { value: "0", text: "0%" },
    { value: "25", text: "25%" },
    { value: "50", text: "50%" },
    { value: "75", text: "75%" },
    { value: "100", text: "100%" },
    { value: "Other", text: "Other" }
  ]

  const getSchoolTypeText = school_type => {
    const selectedSchoolType = schoolTypeOptions.find(
      option => option.value === school_type.value
    )
    if (selectedSchoolType) {
      return selectedSchoolType.text
    } else {
      return "private school"
    }
  }
  const getAmountOptions = school_type => {
    if (school_type === "primary") {
      return [
        { value: "5,000", text: "Least Expensive ($5,000)", type: "least" },
        {
          value: "10,000",
          text: "Average Cost ($10,000)",
          type: "average"
        },
        {
          value: "20,000",
          text: "Most Expensive ($20,000)",
          type: "expensive"
        },
        { value: "Other", text: "Other" }
      ]
    } else if (school_type === "secondary") {
      return [
        {
          value: "10,000",
          text: "Least Expensive ($10,000)",
          type: "least"
        },
        {
          value: "15,000",
          text: "Average Cost ($15,000)",
          type: "average"
        },
        {
          value: "30,000",
          text: "Most Expensive ($30,000)",
          type: "expensive"
        },
        { value: "Other", text: "Other" }
      ]
    } else return []
  }
  if (!isNew) {
    const externalPercentChoice =
      externalPercentOptions.findIndex(
        ({ value }) => value === goal.external_percent.toString()
      ) >= 0
        ? goal.external_percent
        : "Other"
    const amountChoice =
      getAmountOptions(goal.school_type).findIndex(
        ({ value }) => value === goal.amount.toLocaleString()
      ) >= 0
        ? goal.amount.toLocaleString()
        : "Other"
    values = Object.assign({}, goal, {
      externalPercentChoice,
      amountChoice
    })
  }
  const submit = isNew
    ? async values =>
        await addGoal({
          scenario_id: scenario.id,
          household_id: household.id,
          ...values
        })
    : async values => await updateGoal({ id: goal.id, ...values })

  const onDelete = async () => {
    await deleteGoal({ id: goal.id, scenario_id: scenario.id })
    return true
  }
  const getPersonYearOptions = person_id => {
    const currentYear = new Date().getFullYear()
    if (!person_id) {
      return Array(26)
        .fill()
        .map((_, index) => ({
          value: `${currentYear + index}`,
          text: currentYear + index
        }))
    }
    const { birth_date } = people.find(({ id }) => id === person_id)
    const personBirthYear = new Date(birth_date).getFullYear()
    const maxAgeYear = personBirthYear + 21
    const minAgeYear =
      currentYear < personBirthYear + 5 ? personBirthYear + 5 : currentYear
    const yearCount = maxAgeYear - minAgeYear + 1
    return Array(yearCount)
      .fill()
      .map((_, index) => ({
        value: `${minAgeYear + index}`,
        text: `${minAgeYear + index} (age ${minAgeYear +
          index -
          personBirthYear})`
      }))
  }
  const getYearDurationOptions = (person_id, start_year) => {
    if (!person_id) {
      return Array(15)
        .fill(0)
        .map((_, i) => {
          const value = i + 1 + ""
          return { value, text: `${value} Years` }
        })
    }

    const { birth_date } = people.find(({ id }) => id === person_id)
    const birthYear = new Date(birth_date).getFullYear()
    const maximum_duration = birthYear + 21 - start_year + 1
    return Array(maximum_duration)
      .fill()
      .map((_, index) => ({
        value: `${index + 1}`,
        text: `${index + 1} years (age ${parseInt(start_year, 10) +
          index -
          birthYear})`
      }))
  }
  return (
    <PrivateSchoolFormContainer
      {...{
        submit,
        onDelete,
        values,
        schoolTypeOptions,
        peopleDropdownOptions,
        monthOptions,
        externalPercentOptions,
        isNew,
        getPersonYearOptions,
        getAmountOptions,
        getSchoolTypeText,
        getYearDurationOptions,
        ...formProps
      }}
    />
  )
}

export default PrivateSchool
